import { supabase } from '../lib/supabase';

/**
 * Email Service for BarterTrade
 * Handles all automated email notifications via Resend
 */

const EDGE_FUNCTION_URL = import.meta.env?.VITE_SUPABASE_URL + '/functions/v1/send-email';

/**
 * Send welcome email to new users
 */
export const sendWelcomeEmail = async (userEmail, userName) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: {
        emailType: 'welcome',
        recipientEmail: userEmail,
        recipientName: userName,
        data: {
          dashboardUrl: `${window.location?.origin}/dashboard`,
          listingsUrl: `${window.location?.origin}/listings`,
          createListingUrl: `${window.location?.origin}/create-listing`,
          profileUrl: `${window.location?.origin}/profile-settings`,
          tradingGroupsUrl: `${window.location?.origin}/trading-groups-directory`
        }
      }
    });

    if (error) {
      // Check if it's a configuration error (don't log as error, just skip gracefully)
      if (error?.message?.includes('not configured') || 
          error?.message?.includes('RESEND_API_KEY') || 
          error?.message?.includes('non-2xx status code')) {
        // Return success to not block registration
        return { success: true, skipped: true, reason: 'Email service not configured' };
      }
      
      // For other unexpected errors, log but don't block registration
      console.warn('⚠️ Failed to send welcome email, but registration completed successfully.');
      return { success: true, skipped: true, reason: error?.message };
    }
    
    return { success: true, data };
  } catch (error) {
    // Catch block for unexpected errors - handle gracefully

    // Don't block registration - return success with skipped flag
    return { success: true, skipped: true, error: error?.message };
  }
};

/**
 * Send transaction notification email
 */
export const sendTransactionEmail = async (recipientEmail, recipientName, transactionData) => {
  try {
    const emailType = transactionData?.status === 'completed' ? 'transaction_completed' : 'transaction_created';
    
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: {
        emailType,
        recipientEmail,
        recipientName,
        data: {
          transactionId: transactionData?.id,
          otherPartyName: transactionData?.otherPartyName,
          status: transactionData?.status,
          description: transactionData?.description,
          completedAt: transactionData?.completedAt,
          transactionUrl: `${window.location?.origin}/transaction-history`,
          reviewUrl: `${window.location?.origin}/review-submission?transactionId=${transactionData?.id}`
        }
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send transaction email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send review notification email
 */
export const sendReviewReceivedEmail = async (recipientEmail, recipientName, reviewData) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: {
        emailType: 'review_received',
        recipientEmail,
        recipientName,
        data: {
          reviewerName: reviewData?.reviewerName,
          rating: reviewData?.rating,
          title: reviewData?.title,
          comment: reviewData?.comment,
          reviewUrl: `${window.location?.origin}/review-management`
        }
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send review notification email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send listing offer notification email
 */
export const sendListingOfferEmail = async (recipientEmail, recipientName, offerData) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: {
        emailType: 'listing_offer',
        recipientEmail,
        recipientName,
        data: {
          listingTitle: offerData?.listingTitle,
          offerFrom: offerData?.offerFrom,
          offerDetails: offerData?.offerDetails,
          listingUrl: `${window.location?.origin}/item-detail/${offerData?.listingId}`
        }
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send listing offer email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send general notification email
 */
export const sendNotificationEmail = async (recipientEmail, recipientName, notificationData) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: {
        emailType: 'notification',
        recipientEmail,
        recipientName,
        data: {
          title: notificationData?.title,
          message: notificationData?.message,
          actionUrl: notificationData?.actionUrl
        }
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send notification email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send new message notification email
 */
export const sendNewMessageEmail = async (recipientEmail, recipientName, messageData) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: {
        emailType: 'new_message',
        recipientEmail,
        recipientName,
        data: {
          senderName: messageData?.senderName,
          listingTitle: messageData?.listingTitle,
          messagePreview: messageData?.messagePreview || messageData?.content?.substring(0, 100),
          messageUrl: `${window.location?.origin}/messages?conversationId=${messageData?.conversationId}`
        }
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send new message email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send donation confirmation email
 */
export const sendDonationConfirmationEmail = async (recipientEmail, recipientName, donationData) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: {
        emailType: 'donation_confirmation',
        recipientEmail,
        recipientName,
        data: {
          amount: donationData?.amount,
          currency: donationData?.currency || 'usd',
          donationType: donationData?.donationType || 'one_time',
          tierName: donationData?.tierName,
          donationDate: donationData?.donationDate || new Date()?.toLocaleDateString(),
          donationMessage: donationData?.donorMessage,
          receiptUrl: donationData?.receiptUrl,
          dashboardUrl: `${window.location?.origin}/dashboard`
        }
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send donation confirmation email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send monthly donation subscription reminder
 */
export const sendDonationSubscriptionReminder = async (recipientEmail, recipientName, subscriptionData) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: {
        emailType: 'donation_subscription_reminder',
        recipientEmail,
        recipientName,
        data: {
          amount: subscriptionData?.amount,
          currency: subscriptionData?.currency || 'usd',
          paymentDate: subscriptionData?.paymentDate || new Date()?.toLocaleDateString(),
          nextPaymentDate: subscriptionData?.nextPaymentDate,
          receiptUrl: subscriptionData?.receiptUrl,
          manageSubscriptionUrl: `${window.location?.origin}/dashboard`
        }
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send donation subscription reminder:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send group activity summary email
 */
export const sendGroupActivitySummaryEmail = async (recipientEmail, recipientName, summaryData) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: {
        emailType: 'group_activity_summary',
        recipientEmail,
        recipientName,
        data: {
          groupId: summaryData?.groupId,
          groupName: summaryData?.groupName,
          periodStart: summaryData?.periodStart,
          periodEnd: summaryData?.periodEnd,
          counts: summaryData?.counts,
          recentMembers: summaryData?.recentMembers,
          recentListings: summaryData?.recentListings,
          recentDiscussions: summaryData?.recentDiscussions,
          upcomingEvents: summaryData?.upcomingEvents,
          groupUrl: `${window.location?.origin}/trading-group-detail?groupId=${summaryData?.groupId}`,
          preferencesUrl: `${window.location?.origin}/profile-settings`
        }
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send group activity summary email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send email verification link to newly registered users
 */
export const sendVerificationEmail = async (userEmail, userName, confirmationUrl) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: {
        emailType: 'verification_email',
        recipientEmail: userEmail,
        recipientName: userName,
        data: {
          confirmationUrl
        }
      }
    });

    if (error) {
      if (error?.message?.includes('not configured') || 
          error?.message?.includes('RESEND_API_KEY') || 
          error?.message?.includes('non-2xx status code')) {
        return { success: true, skipped: true, reason: 'Email service not configured' };
      }
      console.warn('⚠️ Failed to send verification email:', error?.message);
      return { success: false, skipped: true, reason: error?.message };
    }

    return { success: true, data };
  } catch (error) {
    return { success: true, skipped: true, error: error?.message };
  }
};

/**
 * Helper function to get user email by ID
 */
export const getUserEmailById = async (userId) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select('email, full_name')?.eq('id', userId)?.single();

    if (error) throw error;
    return { email: data?.email, name: data?.full_name };
  } catch (error) {
    console.error('Failed to fetch user email:', error);
    return null;
  }
};

/**
 * Send contact form submission email to admin
 */
export const sendContactFormEmail = async (formData) => {
  const SUBJECT_LABELS = {
    help: 'Help Centre / General Question',
    contact: 'Contact Us / General Enquiry',
    report: 'Report an Issue',
    account: 'Account Problem',
    listing: 'Listing Issue',
    other: 'Other',
  };

  try {
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: {
        emailType: 'contact_form',
        recipientEmail: formData?.email,
        recipientName: formData?.name,
        data: {
          subject: formData?.subject,
          subjectLabel: SUBJECT_LABELS?.[formData?.subject] || formData?.subject,
          message: formData?.message,
        }
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send contact form email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send password reset email
 */
export const sendPasswordResetEmail = async (userEmail, userName, resetData) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: {
        emailType: 'password_reset',
        recipientEmail: userEmail,
        recipientName: userName,
        data: {
          resetUrl: resetData?.resetUrl,
          expiresIn: resetData?.expiresIn || '1 hour'
        }
      }
    });

    if (error) {
      if (error?.message?.includes('not configured') || error?.message?.includes('RESEND_API_KEY')) {
        return { success: true, skipped: true, reason: 'Email service not configured' };
      }
      console.warn('⚠️ Failed to send password reset email:', error?.message);
      return { success: false, error: error?.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Failed to send password reset email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send listing expiry warning email
 */
export const sendListingExpiryWarningEmail = async (userEmail, userName, listingData) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: {
        emailType: 'listing_expiry_warning',
        recipientEmail: userEmail,
        recipientName: userName,
        data: {
          listingTitle: listingData?.title,
          category: listingData?.category,
          expiryDate: listingData?.expiryDate,
          daysUntilExpiry: listingData?.daysUntilExpiry,
          viewCount: listingData?.viewCount,
          offerCount: listingData?.offerCount,
          listingUrl: `${window.location?.origin}/item-detail/${listingData?.id}`,
          renewUrl: `${window.location?.origin}/create-listing?renew=${listingData?.id}`,
          dashboardUrl: `${window.location?.origin}/dashboard`,
          preferencesUrl: `${window.location?.origin}/profile-settings`
        }
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send listing expiry warning email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send transaction confirmation email (covers pending, accepted, completed, cancelled states)
 */
export const sendTransactionConfirmationEmail = async (userEmail, userName, transactionData) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: {
        emailType: 'transaction_confirmation',
        recipientEmail: userEmail,
        recipientName: userName,
        data: {
          transactionId: transactionData?.id,
          status: transactionData?.status,
          otherPartyName: transactionData?.otherPartyName,
          listingTitle: transactionData?.listingTitle,
          offeredItem: transactionData?.offeredItem,
          role: transactionData?.role || 'participant',
          transactionDate: transactionData?.transactionDate || new Date()?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
          transactionUrl: `${window.location?.origin}/transaction-history`,
          reviewUrl: `${window.location?.origin}/review-submission?transactionId=${transactionData?.id}`,
          preferencesUrl: `${window.location?.origin}/profile-settings`
        }
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send transaction confirmation email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send review notification email (rich version with category ratings)
 */
export const sendReviewNotificationEmail = async (userEmail, userName, reviewData) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      body: {
        emailType: 'review_notification',
        recipientEmail: userEmail,
        recipientName: userName,
        data: {
          reviewerName: reviewData?.reviewerName,
          rating: reviewData?.rating,
          reviewTitle: reviewData?.title,
          reviewComment: reviewData?.comment,
          communicationRating: reviewData?.communicationRating,
          conditionRating: reviewData?.conditionRating,
          timelinessRating: reviewData?.timelinessRating,
          reviewDate: reviewData?.reviewDate || new Date()?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
          reviewUrl: `${window.location?.origin}/review-management`,
          profileUrl: `${window.location?.origin}/profile-settings`,
          preferencesUrl: `${window.location?.origin}/profile-settings`
        }
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send review notification email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Check listings nearing expiry and send warning emails
 * Call this from a scheduled job or admin panel
 */
export const checkAndSendListingExpiryWarnings = async () => {
  try {
    const { supabase: sb } = await import('../lib/supabase');
    const now = new Date();
    const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const in3Days = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
    const in1Day = new Date(now.getTime() + 1 * 24 * 60 * 60 * 1000);

    // Fetch active listings expiring within 7 days
    const { data: expiringListings, error } = await supabase
      ?.from('listings')
      ?.select(`
        id, title, category, expires_at, view_count,
        user:user_id (id, full_name, email)
      `)
      ?.eq('is_active', true)
      ?.lte('expires_at', in7Days?.toISOString())
      ?.gte('expires_at', now?.toISOString());

    if (error) throw error;

    const results = [];
    for (const listing of expiringListings || []) {
      const expiresAt = new Date(listing?.expires_at);
      const msLeft = expiresAt - now;
      const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));

      // Only send at 7-day, 3-day, and 1-day thresholds
      if (![7, 3, 1]?.includes(daysLeft)) continue;

      const userEmail = listing?.user?.email;
      const userName = listing?.user?.full_name;
      if (!userEmail) continue;

      const result = await sendListingExpiryWarningEmail(userEmail, userName, {
        id: listing?.id,
        title: listing?.title,
        category: listing?.category,
        expiryDate: expiresAt?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
        daysUntilExpiry: daysLeft,
        viewCount: listing?.view_count || 0
      });

      results?.push({ listingId: listing?.id, daysLeft, result });
    }

    return { success: true, processed: results?.length, results };
  } catch (error) {
    console.error('Failed to check listing expiry warnings:', error);
    return { success: false, error: error?.message };
  }
};

export default {
  sendWelcomeEmail,
  sendTransactionEmail,
  sendReviewReceivedEmail,
  sendListingOfferEmail,
  sendNotificationEmail,
  sendNewMessageEmail,
  sendDonationConfirmationEmail,
  sendDonationSubscriptionReminder,
  sendGroupActivitySummaryEmail,
  sendPasswordResetEmail,
  sendListingExpiryWarningEmail,
  sendTransactionConfirmationEmail,
  sendReviewNotificationEmail,
  checkAndSendListingExpiryWarnings,
  getUserEmailById
};